import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Save, Building, Phone, MapPin, Globe, Shield, Clock, Facebook, Twitter, Linkedin, Instagram, Youtube, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const CompanyInfo: React.FC = () => {
  const { showToast } = useToast();
  const [companyInfo, setCompanyInfo] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Social media state as an array for easier UI management
  const [socialMedia, setSocialMedia] = useState<{ platform: string, url: string }[]>([]);
  // Business hours state
  const [businessHours, setBusinessHours] = useState<{ day: string, hours: string }[]>([]);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCompanyInfo();
      setCompanyInfo(data || null);

      if (data) {
        // Parse social media
        if (data.social_media && typeof data.social_media === 'object') {
          const sm = Object.entries(data.social_media).map(([platform, url]) => ({
            platform,
            url: url as string
          }));
          setSocialMedia(sm);
        }

        // Parse business hours
        if (data.business_hours && typeof data.business_hours === 'object') {
          const bh = Object.entries(data.business_hours).map(([day, hours]) => ({
            day,
            hours: hours as string
          }));
          setBusinessHours(bh);
        } else if (!data.business_hours) {
          // Initialize default if empty
          setBusinessHours([
            { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM' },
            { day: 'Saturday', hours: '10:00 AM - 4:00 PM' },
            { day: 'Sunday', hours: 'Closed' }
          ]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData(e.currentTarget);

    // Convert social media array back to object
    const smObj: Record<string, string> = {};
    socialMedia.forEach(item => {
      if (item.platform.trim() && item.url.trim()) {
        smObj[item.platform.toLowerCase()] = item.url;
      }
    });
    formData.set('social_media', JSON.stringify(smObj));

    // Convert business hours array back to object
    const bhObj: Record<string, string> = {};
    businessHours.forEach(item => {
      if (item.day.trim() && item.hours.trim()) {
        bhObj[item.day] = item.hours;
      }
    });
    formData.set('business_hours', JSON.stringify(bhObj));

    try {
      if (companyInfo?.id) {
        await apiService.updateCompanyInfo(companyInfo.id, formData);
      } else {
        await apiService.createCompanyInfo(formData);
      }

      showToast('Company information updated!', 'success');
      fetchCompanyInfo();
    } catch (error) {
      console.error('Failed to save company info:', error);
      showToast('Error updating company information.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addSocial = () => setSocialMedia([...socialMedia, { platform: '', url: '' }]);
  const removeSocial = (index: number) => setSocialMedia(socialMedia.filter((_, i) => i !== index));
  const updateSocial = (index: number, field: 'platform' | 'url', value: string) => {
    const newSm = [...socialMedia];
    newSm[index][field] = value;
    setSocialMedia(newSm);
  };

  const addHour = () => setBusinessHours([...businessHours, { day: '', hours: '' }]);
  const removeHour = (index: number) => setBusinessHours(businessHours.filter((_, i) => i !== index));
  const updateHour = (index: number, field: 'day' | 'hours', value: string) => {
    const newBh = [...businessHours];
    newBh[index][field] = value;
    setBusinessHours(newBh);
  };

  const getPlatformIcon = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('facebook')) return <Facebook className="h-4 w-4 text-blue-600" />;
    if (p.includes('twitter') || p.includes('x')) return <Twitter className="h-4 w-4 text-black" />;
    if (p.includes('linkedin')) return <Linkedin className="h-4 w-4 text-blue-700" />;
    if (p.includes('instagram')) return <Instagram className="h-4 w-4 text-pink-600" />;
    if (p.includes('youtube')) return <Youtube className="h-4 w-4 text-red-600" />;
    return <Globe className="h-4 w-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Company Information</h1>
        <p className="text-gray-600">Global contact details, social links, and business hours</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Building className="h-5 w-5 text-orange-600" /> Basic Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input name="company_name" defaultValue={companyInfo?.company_name} required className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                  <input name="tagline" defaultValue={companyInfo?.tagline} className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">About Short Text</label>
                  <textarea name="description" defaultValue={companyInfo?.description} required rows={3} className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Globe className="h-5 w-5 text-blue-600" /> Social Links</h3>
                  <button type="button" onClick={addSocial} className="text-xs font-bold text-orange-600 flex items-center gap-1 hover:bg-orange-50 px-2 py-1 rounded transition">
                    <Plus className="h-3 w-3" /> Add Link
                  </button>
                </div>
                <div className="space-y-3">
                  {socialMedia.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <div className="w-1/3 relative">
                        <div className="absolute left-2.5 top-1/2 -translate-y-1/2">
                          {getPlatformIcon(item.platform)}
                        </div>
                        <input
                          placeholder="Platform"
                          value={item.platform}
                          onChange={(e) => updateSocial(idx, 'platform', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg pl-8 pr-2 py-1.5 text-xs font-medium"
                        />
                      </div>
                      <input
                        placeholder="URL (https://...)"
                        value={item.url}
                        onChange={(e) => updateSocial(idx, 'url', e.target.value)}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs"
                      />
                      <button type="button" onClick={() => removeSocial(idx)} className="text-gray-300 hover:text-red-500 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {socialMedia.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-4 bg-gray-50 rounded-lg border border-dashed">No social links added</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Clock className="h-5 w-5 text-green-600" /> Business Hours</h3>
                  <button type="button" onClick={addHour} className="text-xs font-bold text-orange-600 flex items-center gap-1 hover:bg-orange-50 px-2 py-1 rounded transition">
                    <Plus className="h-3 w-3" /> Add Row
                  </button>
                </div>
                <div className="space-y-3">
                  {businessHours.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        placeholder="Day(s)"
                        value={item.day}
                        onChange={(e) => updateHour(idx, 'day', e.target.value)}
                        className="w-2/5 border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-medium"
                      />
                      <input
                        placeholder="Hours (e.g. 9AM-5PM)"
                        value={item.hours}
                        onChange={(e) => updateHour(idx, 'hours', e.target.value)}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs"
                      />
                      <button type="button" onClick={() => removeHour(idx)} className="text-gray-300 hover:text-red-500 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {businessHours.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-4 bg-gray-50 rounded-lg border border-dashed">No business hours added</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Phone className="h-5 w-5 text-gray-400" /> Contact Info</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Primary Phone</label>
                    <input name="phone" defaultValue={companyInfo?.phone} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Public Email</label>
                    <input name="email" type="email" defaultValue={companyInfo?.email} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Emergency Line</label>
                    <input name="emergency_contact" defaultValue={companyInfo?.emergency_contact} className="w-full border border-red-100 bg-red-50/20 rounded-lg px-4 py-2" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><MapPin className="h-5 w-5 text-gray-400" /> Location</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Full Address</label>
                    <textarea name="address" defaultValue={companyInfo?.address} required rows={2} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Website URL</label>
                    <input name="website" type="url" defaultValue={companyInfo?.website} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Company Logo</h3>
              <div className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center border mb-4 shadow-inner overflow-hidden">
                {companyInfo?.logo ? (
                  <img src={getImageUrl(companyInfo.logo) || undefined} alt="Logo" className="max-h-full max-w-full p-4 object-contain" />
                ) : (
                  <Building className="h-10 w-10 text-gray-200" />
                )}
              </div>
              <input type="file" name="logo" accept="image/*" className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 transition shadow-sm" />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Shield className="h-5 w-5 text-gray-400" /> Legal</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">License #</label>
                  <input name="license_number" defaultValue={companyInfo?.license_number} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Insurance</label>
                  <input name="insurance_info" defaultValue={companyInfo?.insurance_info} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
              </div>
            </div>

            <button type="submit" disabled={saving} className="w-full py-4 bg-orange-600 hover:bg-orange-700 rounded-2xl font-bold text-white transition flex items-center justify-center gap-2 shadow-lg shadow-orange-100 active:scale-95 transform">
              {saving ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white" /> : <Save className="h-5 w-5" />}
              {saving ? 'Publishing Changes...' : 'Save Company Profile'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CompanyInfo;

